import type {AudioBufferSink} from 'mediabunny';

export async function readMonoWindowFromSink(sink: AudioBufferSink, startSec: number, durationSec: number, sampleRate?: number): Promise<Float32Array | null> {
    const sr = sampleRate || 48000;
    const framesNeeded = Math.max(1, Math.floor(durationSec * sr));
    const iter = sink.buffers(Math.max(0, startSec));
    let accFrames = 0;
    let mono: Float32Array | null = null;
    
    for await (const {buffer} of iter as any) {
        if (!buffer) break;
        const frames = buffer.length;
        const channels = buffer.numberOfChannels;
        const take = Math.min(frames, framesNeeded - accFrames);
        if (take <= 0) break;
        const chunk = new Float32Array(take);
        // Downmix to mono by averaging channels
        for (let ch = 0; ch < channels; ch++) {
            const data = buffer.getChannelData(ch);
            for (let i = 0; i < take; i++) chunk[i] += data[i] / channels;
        }
        if (!mono) mono = new Float32Array(framesNeeded);
        mono.set(chunk, accFrames);
        accFrames += take;
        if (accFrames >= framesNeeded) break;
    }
    try { await (iter as any)?.return?.(); } catch {}

    if (!mono || mono.length === 0) {
        console.log("FOUND NOTHING reading audio buffer sink");
        return null;
    }


    return mono;
}

export function findNearestZeroCross(samples: Float32Array, centerIndex: number): number | null {
    const n = samples.length;
    const radius = Math.min(centerIndex, n - 1 - centerIndex);
    let bestIdx: number | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let r = 0; r <= radius; r++) {
        const i1 = centerIndex - r;
        const i2 = i1 + 1;
        if (i1 < 0 || i2 >= n) continue;
        const s1 = samples[i1];
        const s2 = samples[i2];
        if (s1 === 0) return i1;
        if ((s1 < 0 && s2 > 0) || (s1 > 0 && s2 < 0)) {
            const t = Math.abs(s1) / (Math.abs(s1) + Math.abs(s2));
            const z = i1 + t;
            const dist = Math.abs(z - centerIndex);
            if (dist < bestDist) { bestDist = dist; bestIdx = z; }
        }
    }
    if (bestIdx == null) {
        let idx = centerIndex;
        let minAbs = Math.abs(samples[idx]);
        const radius2 = Math.min(200, Math.max(centerIndex, n - centerIndex));
        for (let r = 1; r <= radius2; r++) {
            const left = centerIndex - r;
            const right = centerIndex + r;
            if (left >= 0 && Math.abs(samples[left]) < minAbs) { idx = left; minAbs = Math.abs(samples[left]); }
            if (right < n && Math.abs(samples[right]) < minAbs) { idx = right; minAbs = Math.abs(samples[right]); }
        }
        return idx;
    }
    return (bestIdx as number) | 0;
}

export function rms(samples: Float32Array, start: number, len: number): number {
    const n = samples.length;
    if (start < 0) start = 0;
    if (start >= n) return 0;
    const end = Math.min(n, start + Math.max(1, len));
    let sum = 0;
    for (let i = start; i < end; i++) sum += samples[i] * samples[i];
    return Math.sqrt(sum / (end - start));
}

export async function analyzeSegmentJoinsWithSink(
    sink: AudioBufferSink,
    segments: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number; fadeInMs?: number; fadeOutMs?: number }>,
    sampleRate?: number
): Promise<void> {
    if (!segments || segments.length <= 1) return;
    const sr = sampleRate || 48000;
    const searchWindowSec = 0.02;
    const halfWin = searchWindowSec / 2;
    const rmsWinSec = 0.01;
    const baseMs = 10;
    const maxSnap = 0.005;

    for (let i = 1; i < segments.length; i++) {
        const prev = segments[i - 1];
        const curr = segments[i];
        const boundarySrcEnd = prev.srcEndSec ?? 0;
        const boundarySrcStart = curr.srcStartSec ?? 0;

        const leftMono = await readMonoWindowFromSink(sink, boundarySrcEnd - halfWin, searchWindowSec, sr);
        const rightMono = await readMonoWindowFromSink(sink, boundarySrcStart - halfWin, searchWindowSec, sr);
        if (!leftMono || !rightMono) continue;

        const leftIdx = findNearestZeroCross(leftMono, Math.floor(leftMono.length / 2));
        const rightIdx = findNearestZeroCross(rightMono, Math.floor(rightMono.length / 2));

        let snappedLeftSec = boundarySrcEnd;
        let snappedRightSec = boundarySrcStart;
        if (leftIdx != null) snappedLeftSec = (boundarySrcEnd - halfWin) + (leftIdx / sr);
        if (rightIdx != null) snappedRightSec = (boundarySrcStart - halfWin) + (rightIdx / sr);

        const leftDelta = Math.max(-maxSnap, Math.min(maxSnap, snappedLeftSec - (prev.srcEndSec ?? snappedLeftSec)));
        const rightDelta = Math.max(-maxSnap, Math.min(maxSnap, snappedRightSec - (curr.srcStartSec ?? snappedRightSec)));
        prev.srcEndSec = Math.max(prev.srcStartSec, (prev.srcEndSec ?? 0) + leftDelta);
        curr.srcStartSec = Math.min(curr.srcEndSec, (curr.srcStartSec ?? 0) + rightDelta);

        const rmsLeft = rms(leftMono, Math.max(0, Math.floor(leftMono.length / 2 - rmsWinSec * sr)), Math.floor(rmsWinSec * sr));
        const rmsRight = rms(rightMono, Math.floor(rightMono.length / 2), Math.floor(rmsWinSec * sr));
        const ratio = (Math.max(rmsLeft, 1e-6)) > 0 ? (rmsRight / Math.max(rmsLeft, 1e-6)) : 1;

        let fadeMs = baseMs;
        if (!Number.isFinite(ratio)) {
            fadeMs = 20;
        } else if (ratio > 2 || ratio < 0.5) {
            fadeMs = 20;
        }
        if (leftIdx == null || rightIdx == null) fadeMs = Math.max(fadeMs, 20);

        prev.fadeOutMs = Math.max(prev.fadeOutMs ?? 0, fadeMs);
        curr.fadeInMs = Math.max(curr.fadeInMs ?? 0, fadeMs);
    }
}


